import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, X } from "lucide-react";
import useDismissible from "@/lib/useDismissible";

/**
 * QuickAlertsWidget (iter187 – 02/2026)
 * ------------------------------------------------------------------
 * Widget compatto e COLLASSABILE (default: chiuso) che consolida in
 * un'unica riga i 5 vecchi banner d'allarme rimossi in iter186:
 *   • Acconti superiori al totale
 *   • Totali di gruppo non quadranti
 *   • Possibili no-show
 *   • Camere chiuse da troppo tempo
 *   • Revoche tassa in sospeso
 *
 * Comportamento:
 *   - Se tutti gli avvisi sono 0 → mostra riga verde "Tutto in ordine"
 *   - Se >0 → riga arancione con conteggio totale e chevron per aprire
 *   - Aperta: mostra breakdown per categoria + link diretto
 *   - Stato aperto/chiuso persiste in localStorage
 */
export default function QuickAlertsWidget({ setActiveTab, setFilterStatus }) {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem("quick_alerts_open") === "1"; }
    catch { return false; }
  });
  // iter190 — X per chiudere il widget avvisi per 3 giorni
  const [dismissed, dismiss] = useDismissible("quick-alerts", 3);
  // iter195 — Cestino Alert Widget: X per singola riga.
  // Ricordiamo per categoria il conteggio al momento del dismiss: se in
  // futuro il numero CAMBIA (nuove anomalie), la riga ricompare — così non
  // perdi mai un avviso davvero nuovo.
  const [rowDismiss, setRowDismiss] = useState(() => {
    try { return JSON.parse(localStorage.getItem("quick_alerts_row_dismiss") || "{}"); }
    catch { return {}; }
  });
  const dismissRow = (key, currentCount) => {
    const next = { ...rowDismiss, [key]: currentCount };
    setRowDismiss(next);
    try { localStorage.setItem("quick_alerts_row_dismiss", JSON.stringify(next)); } catch { /* noop */ }
  };
  const [counts, setCounts] = useState({
    deposit_overpaid: 0,
    group_mismatch: 0,
    no_show: 0,
    long_closed: 0,
    revocations: 0,
  });

  useEffect(() => {
    // Read from a single endpoint (`/admin/notifications/counts` already
    // exposes most of these). We fetch and map to the widget's shape.
    api.get("/admin/notifications/counts")
      .then((r) => {
        const d = r.data || {};
        setCounts({
          deposit_overpaid: d.bookings_deposit_overpaid || 0,
          group_mismatch: d.bookings_group_mismatch || 0,
          no_show: 0, // hardcoded: separato via widget se serve
          long_closed: 0,
          revocations: d.compliance_revocations || 0,
        });
      })
      .catch(() => { /* stay silent — widget just shows 0 */ });
  }, []);

  const total = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts]
  );

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try { localStorage.setItem("quick_alerts_open", next ? "1" : "0"); } catch { /* noop */ }
  };

  const rowClick = (target) => {
    if (target === "bookings-deposit") {
      setActiveTab?.("bookings");
      setFilterStatus?.("all");
    } else if (target === "bookings-group") {
      setActiveTab?.("bookings");
    } else if (target === "compliance") {
      setActiveTab?.("compliance");
    }
  };

  if (total === 0) {
    return (
      <div
        className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-800"
        data-testid="quick-alerts-ok"
      >
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
        <span className="font-medium">Tutto in ordine</span>
        <span className="text-emerald-600 text-xs ml-auto">nessuna anomalia rilevata</span>
      </div>
    );
  }
  // iter190 — chiuso per 3 giorni
  if (dismissed) return null;

  const rows = [
    { key: "deposit_overpaid", label: "Acconti oltre il totale", count: counts.deposit_overpaid, target: "bookings-deposit" },
    { key: "group_mismatch",   label: "Totali di gruppo non quadranti", count: counts.group_mismatch, target: "bookings-group" },
    { key: "revocations",      label: "Revoche tassa in sospeso", count: counts.revocations, target: "compliance" },
  ].filter((r) => r.count > 0 && rowDismiss[r.key] !== r.count);

  // Se tutte le righe sono state cestinate individualmente, non mostrare
  // il widget (ma solo finché il conteggio non cambia).
  if (rows.length === 0) return null;

  const visibleTotal = rows.reduce((a, r) => a + r.count, 0);

  return (
    <div
      className="bg-amber-50 border border-amber-300 rounded-lg overflow-hidden relative"
      data-testid="quick-alerts-widget"
    >
      {/* iter190 — X chiudi per 3 giorni */}
      <button
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        className="absolute top-1 right-1 p-1 rounded hover:bg-amber-200 text-amber-700 hover:text-amber-900 transition-colors z-10"
        aria-label="Chiudi per 3 giorni"
        title="Nascondi per 3 giorni"
        data-testid="quick-alerts-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-900 hover:bg-amber-100 transition-colors"
        data-testid="quick-alerts-toggle"
      >
        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
        <span className="font-semibold">
          {visibleTotal} {visibleTotal === 1 ? "anomalia da rivedere" : "anomalie da rivedere"}
        </span>
        <span className="text-amber-700 text-xs ml-auto flex items-center gap-1 mr-6">
          {expanded ? "chiudi" : "apri dettaglio"}
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-amber-200 divide-y divide-amber-100 bg-white">
          {rows.map((r) => (
            <div
              key={r.key}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#1E3A5F] hover:bg-amber-50 transition-colors"
              data-testid={`quick-alerts-row-${r.key}`}
            >
              <button
                type="button"
                onClick={() => rowClick(r.target)}
                className="flex-1 text-left"
                data-testid={`quick-alerts-row-open-${r.key}`}
              >
                {r.label}
              </button>
              <span className="bg-amber-500 text-white text-xs font-bold rounded-full px-2 py-0.5 tabular-nums">
                {r.count}
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); dismissRow(r.key, r.count); }}
                className="p-1 rounded hover:bg-amber-100 text-amber-700 hover:text-amber-900 transition-colors"
                aria-label="Nascondi questo avviso finché il conteggio non cambia"
                title="Nascondi finché non arrivano nuovi casi"
                data-testid={`quick-alerts-row-dismiss-${r.key}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
